import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';

const fadeUp = (delay: number, v: boolean): React.CSSProperties => ({
  opacity: v ? 1 : 0,
  transform: v ? 'translateY(0)' : 'translateY(22px)',
  filter: v ? 'blur(0px)' : 'blur(7px)',
  transition: `opacity 0.9s ease ${delay}ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}ms, filter 0.9s ease ${delay}ms`,
});

const revealUp = (delay: number, v: boolean): React.CSSProperties => ({
  display: 'block',
  transform: v ? 'translateY(0)' : 'translateY(110%)',
  transition: `transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
});

export default function ValuePillars() {
  const { ref: headerRef, isVisible: hv } = useScrollAnimation(0.1);
  const { ref: gridRef, isVisible: gv } = useScrollAnimation(0.04);
  const { t } = useTranslation();

  const pillars = [
    {
      index: '01',
      category: t('vp_p1_cat'),
      title: t('vp_p1_title'),
      body: t('vp_p1_body'),
      highlight: t('vp_p1_highlight'),
      icon: 'ri-filter-3-line',
      stat: t('vp_p1_stat'),
      statLabel: t('vp_p1_stat_label'),
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/b730523fea024a4c4886c27d4f5f0e40.jpeg',
    },
    {
      index: '02',
      category: t('vp_p2_cat'),
      title: t('vp_p2_title'),
      body: t('vp_p2_body'),
      highlight: t('vp_p2_highlight'),
      icon: 'ri-camera-off-line',
      stat: t('vp_p2_stat'),
      statLabel: t('vp_p2_stat_label'),
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/0d7e9fff59ddf8be36ca45bf12c3a543.jpeg',
    },
    {
      index: '03',
      category: t('vp_p3_cat'),
      title: t('vp_p3_title'),
      body: t('vp_p3_body'),
      highlight: t('vp_p3_highlight'),
      icon: 'ri-scales-3-line',
      stat: t('vp_p3_stat'),
      statLabel: t('vp_p3_stat_label'),
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/8d742ec872f17b3385356fde0dc7eff8.jpeg',
    },
  ];

  return (
    <section id="values" className="w-full bg-white pt-28 lg:pt-36 pb-10 lg:pb-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        <div ref={headerRef} className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20">
          <div>
            <p className="font-label text-xs tracking-[0.3em] text-stone-400 uppercase mb-4" style={fadeUp(0, hv)}>
              {t('vp_label')}
            </p>
            <div className="flex items-center gap-5">
              <div
                className="w-1 rounded-full flex-shrink-0 bg-stone-900 origin-top"
                style={{ height: hv ? '3rem' : '0', transition: 'height 0.9s cubic-bezier(0.16,1,0.3,1) 180ms' }}
              />
              <div className="overflow-hidden">
                <h2 className="font-zh-serif text-4xl lg:text-5xl text-stone-900 font-light" style={revealUp(220, hv)}>
                  {t('vp_title')}
                </h2>
              </div>
            </div>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-100">
          {pillars.map((p, i) => (
            <div key={p.index} className="group relative bg-white flex flex-col overflow-hidden cursor-default" style={fadeUp(i * 160, gv)}>
              <div className="relative w-full aspect-[4/5] md:aspect-[3/4] lg:aspect-[4/5] overflow-hidden flex-shrink-0">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute top-6 left-6">
                  <span className="font-label text-xs tracking-[0.3em] text-white/60 uppercase">{p.index} / {p.category}</span>
                </div>
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <p className="font-display text-5xl text-white font-light leading-none">{p.stat}</p>
                  <p className="font-label text-xs text-white/60 tracking-wide pb-1 whitespace-nowrap">{p.statLabel}</p>
                </div>
              </div>
              <div className="flex flex-col flex-1 p-6 md:p-7 lg:p-8 bg-white border-t border-stone-100">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-9 h-9 flex items-center justify-center border border-stone-200 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${p.icon} text-base text-stone-600`} />
                  </div>
                  <h3 className="font-zh-serif text-xl text-stone-900 font-medium leading-snug">{p.title}</h3>
                </div>
                <p className="font-noto text-stone-400 text-sm leading-7 font-light mb-6 flex-1">{p.body}</p>
                <div className="border-t border-stone-100 pt-5">
                  <p className="font-zh-serif text-stone-700 text-sm font-medium leading-relaxed">{p.highlight}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 pt-12 border-t border-stone-100 flex flex-col md:flex-row items-center justify-between gap-6" style={fadeUp(200, gv)}>
          <p className="font-zh-serif text-xl md:text-2xl text-stone-800 font-light">{t('vp_bottom')}</p>
          <div className="flex items-center gap-3">
            {pillars.map((p) => (
              <div key={p.index} className="flex items-center gap-2">
                <span className="font-label text-xs tracking-widest text-stone-400 uppercase">{p.category}</span>
                {p.index !== '03' && <span className="w-4 h-px bg-stone-200 inline-block" />}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
